import { COMMA, ENTER, SEMICOLON, SPACE } from '@angular/cdk/keycodes';
import {
  computed,
  inject,
  Injectable,
  InjectionToken,
  OnDestroy,
  signal,
  Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { VirtualFenceService } from '@vectronic/base-map';
import { dsDtos, dsEnums, userDtos, userEnums, webDtos } from '@vectronic/openApi-customer';
import { DIALOG_WIDER_TALL_CONFIG, TourGuideService } from '@vectronic/ui';
import {
  CollarAndGroup,
  DialogService,
  DropOffDataTable,
  ParameterDataService,
  ParameterDataWithParameter,
  ScheduleHelp,
} from '@vectronic/util';
import { isEqual } from 'es-toolkit';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  Subscription,
  take,
} from 'rxjs';
import { CameraScheduleService } from '../../services/camera-schedule.service';
import { RemoteCommandProviderService } from '../remote-command-provider.service';
import { ScheduleServices } from '../schedule.service';
import { CamerasFormatService } from './cameras-format.service';
import { DropOffCommandService } from './drop-off-command.service';
import { ParametersIdentifierService } from './parameters-identifier.service';
import { RemoteCommandsService } from './remote-commands.service';
import { ValidationService } from './validation.service';

export type RemoteCommandModalData = {
  selectedDeviceIds?: number[];
  schedules?: dsDtos.Schedule[];
  parameterDataList?: dsDtos.ParameterData[];
  readonly?: boolean;
  virtualFenceConstructionRules?: dsDtos.VirtualFenceConstructionRule[];
  updateDefault?: boolean;
  remote?: boolean;
  isTerminal?: boolean;
  isEditTerminalCommand?: boolean;
  onWriteTerminalCommand?: (commands: dsDtos.Command[]) => Observable<unknown>;
};

export const REMOTE_COMMAND_MODAL_SERVICE = new InjectionToken<RemoteCommandModalService>(
  'REMOTE_COMMAND_MODAL_SERVICE',
);

@Injectable()
export abstract class RemoteCommandModalService implements OnDestroy {
  remoteCommandsService = inject(RemoteCommandsService);
  validationService = inject(ValidationService);
  dropOffCommandService = inject(DropOffCommandService);
  dialogService = inject(DialogService);
  virtualFenceService = inject(VirtualFenceService);
  scheduleServices = inject(ScheduleServices);
  parameterDataService = inject(ParameterDataService);
  cameraScheduleService = inject(CameraScheduleService);
  parametersIdentifierService = inject(ParametersIdentifierService);
  remoteCommandProviderService = inject(RemoteCommandProviderService);
  camerasFormatService = inject(CamerasFormatService);
  tourGuideService = inject(TourGuideService);
  dialogRef = inject(MatDialogRef);
  data: RemoteCommandModalData = inject(MAT_DIALOG_DATA);

  isFullScreen = signal(false);
  isConfirmed = signal(false);
  isLoading = signal(false);
  selectedTab = signal<string>('');
  showMore = true;
  collarCtrl = new FormControl('');
  separatorKeysCodes: number[] = [ENTER, COMMA, SPACE, SEMICOLON];
  displayClose: boolean;
  selectedCollarsAndGroups$ = new BehaviorSubject<CollarAndGroup[]>([]);

  validationApiSubscriptions: Record<number, Subscription> = {};
  validationSubscription: Subscription;
  dropOffSubscription: Subscription;
  devicesSubscription: Subscription;
  toggleSubscription: Subscription;
  selectionSubscription: Subscription;

  parameters$ = this.remoteCommandProviderService.parameters$;
  readonly filteredParameters$ = combineLatest([
    this.parameters$,
    this.remoteCommandsService.deviceTypeParameterConfig$,
  ]).pipe(
    map(([parameters, parameterResponse]) => {
      if (!parameterResponse?.length) {
        return [];
      }
      return parameters?.filter((parameter) =>
        parameterResponse.some(({ parameterId }) => parameterId === parameter.id),
      );
    }),
  );
  readonly categories$ = this.remoteCommandProviderService.parameterCategories$;
  readonly subsystems$ = this.remoteCommandProviderService.parameterSubsystems$;
  readonly customCategoryCounts$ = this.scheduleServices.filledCountByKey$;
  readonly videoFormatsOptions$ = this.camerasFormatService.videoFormatsOptions$;
  readonly photoFormatsOptions$ = this.camerasFormatService.photoFormatsOptions$;

  parameters = toSignal(this.parameters$);
  filteredParameters = toSignal(this.filteredParameters$);
  categories = toSignal(this.categories$);
  subsystems = toSignal(this.subsystems$);
  selectedCollarIds = toSignal(this.remoteCommandsService.selectedCollarIds$);
  deviceTypeParameterConfig = toSignal(this.remoteCommandsService.deviceTypeParameterConfig$);
  userOrFactory = toSignal(this.remoteCommandsService.userOrFactory$);

  uhfModeParameter = toSignal(this.parametersIdentifierService.uhfModeParameter$);
  uhfStartTimeParameter = toSignal(this.parametersIdentifierService.uhfStartTimeParameter$);
  uhfEndTimeParameter = toSignal(this.parametersIdentifierService.uhfEndTimeParameter$);
  beaconPowerId = toSignal(this.parametersIdentifierService.beaconPowerId$);

  warningByParameterId = computed(() => {
    const warningMap = new Map<string, string>();
    const beaconPowerId = this.beaconPowerId();
    if (beaconPowerId && this.parameterDataService.isParameterChanged(beaconPowerId)) {
      warningMap.set(beaconPowerId, 'Changing beacon power impacts battery life.');
    }
    const uhfModeParam = this.uhfModeParameter();
    const uhfStartParam = this.uhfStartTimeParameter();
    const uhfEndParam = this.uhfEndTimeParameter();

    if (!uhfModeParam?.id) return warningMap;

    const parameterDataList = this.parameterDataService.updatedParameterDataList();
    const uhfModeData = parameterDataList?.find((p) => p.parameterId === uhfModeParam.id);
    const isUhfModeOn = uhfModeData?.data === '1';

    if (!isUhfModeOn) {
      if (uhfStartParam?.id) {
        warningMap.set(uhfStartParam.id, 'UHF Mode must be ON to modify Start time.');
      }
      if (uhfEndParam?.id) {
        warningMap.set(uhfEndParam.id, 'UHF Mode must be ON to modify End time.');
      }
    }

    return warningMap;
  });

  errorByParameterId = computed(() => {
    const errorMap = new Map<string, string>();
    const selectedIds = this.selectedCollarIds();
    const paramConfigs = this.deviceTypeParameterConfig();

    if (!selectedIds?.length || !paramConfigs?.length) return errorMap;

    paramConfigs.forEach((config) => {
      if (!config.parameterId || !config.availableForDevices) return;

      const availableSet = new Set(config.availableForDevices);
      const unavailableIds = selectedIds.filter((id) => !availableSet.has(id));

      if (unavailableIds.length > 0) {
        errorMap.set(
          config.parameterId,
          `This parameter is not available for: ${unavailableIds.join(', ')}`,
        );
      }
    });

    return errorMap;
  });

  disabledByParameterId = computed(() => {
    const disabledMap = new Map<string, boolean>();
    const selectedIds = this.selectedCollarIds();
    const paramConfigs = this.deviceTypeParameterConfig();

    if (!selectedIds?.length || !paramConfigs?.length) return disabledMap;

    paramConfigs.forEach((config) => {
      if (!config.parameterId || !config.availableForDevices) return;

      const availableSet = new Set(config.availableForDevices);
      const isAvailableForAll = selectedIds.every((id) => availableSet.has(id));

      if (!isAvailableForAll) {
        disabledMap.set(config.parameterId, true);
      }
    });

    return disabledMap;
  });

  scheduleResponse = toSignal(this.remoteCommandProviderService.remoteSchedules$);
  deviceCommandResponse = toSignal(this.remoteCommandProviderService.remoteDeviceCommands$);
  videoFormatsOptions = toSignal(this.camerasFormatService.videoFormatsOptions$);
  photoFormatsOptions = toSignal(this.camerasFormatService.photoFormatsOptions$);
  collarDetailsResponse = toSignal(this.remoteCommandProviderService.selectedCollars$);
  hasMultipleCameraTypes = this.remoteCommandsService.hasMultipleCameraTypes;

  virtualFenceNotAvailableForIds = computed(() => {
    const selectedIds = this.selectedCollarIds();
    const scheduleResponse = this.scheduleResponse();

    if (!selectedIds?.length) {
      return [];
    }

    const virtualFenceSupportedIds = new Set(scheduleResponse?.VIRTUAL_FENCE || []);
    return selectedIds.filter((id) => !virtualFenceSupportedIds.has(id));
  });

  isGsmSelected$: Observable<boolean> = combineLatest([
    this.remoteCommandProviderService.selectedCollars$,
    this.remoteCommandsService.selectedCollarIds$,
  ]).pipe(
    map(([collars, selectedIds]) => {
      if (!collars || !selectedIds) return false;
      const selectedSet = new Set(selectedIds);
      return collars?.filter((c) => selectedSet.has(c.id))?.some((c) => c.comType === 'GSM');
    }),
  );

  permanentEndDate$: Observable<string> = this.isGsmSelected$.pipe(
    map((isGsm) => (isGsm ? '2038-01-01' : '2050-01-01')),
  );

  permanentEndDateByCollar$: Observable<Record<number, string>> = combineLatest([
    this.remoteCommandProviderService.selectedCollars$,
    this.remoteCommandsService.selectedCollarIds$,
  ]).pipe(
    map(([collars, selectedIds]) => {
      const mapObj: Record<number, string> = {};
      if (!selectedIds?.length) return mapObj;
      const selectedSet = new Set(selectedIds);
      collars
        ?.filter((c) => selectedSet.has(c.id))
        ?.forEach((c) => {
          mapObj[c.id] = c?.comType === 'GSM' ? '2038-01-01' : '2050-01-01';
        });
      return mapObj;
    }),
  );

  userSubsystemVisibility = computed(() => {
    return [];
  });

  showUserOrFactoryToggle = signal(false);

  canSendCommands = computed(() => true);

  filteredCollars$: Observable<{ id: number; name?: string }[]> = of([]);

  isPositionsLoading: Signal<boolean> = signal(false);

  mapPositions: Signal<webDtos.MapPositionsList[]> = signal<webDtos.MapPositionsList[]>([]);

  mapPreferences: Signal<userDtos.UserPreferences | undefined> = computed(() => undefined);

  userAccess: dsDtos.DeviceTypeParameterConfig.UserAccessEnum[] | undefined = undefined;

  onChangeUserOrFactory(_$event: 'customer' | 'allUser' | 'allFactory'): void {}

  onCollarSelected(_event: { option: { value: any } }): void {}

  detectInitialUserOrFactory(): void {}

  areSchedulesFilled = computed(
    () =>
      ScheduleHelp.services().some((type) => this.scheduleServices[type].isFilledAndChanged()) ||
      this.cameraScheduleService.isFilledAndChanged(),
  );

  isVirtualFenceFilled = computed(() => this.virtualFenceService.isVirtualFenceFilled());

  areSchedulesValid = computed(
    () =>
      ScheduleHelp.services().every((type) => this.scheduleServices[type].isValid()) &&
      this.cameraScheduleService.isValid(),
  );

  isVirtualFenceChanged = computed(() => this.virtualFenceService.isVirtualFenceChanged());

  isChanged = this.remoteCommandsService.isChanged;
  isFilled = this.remoteCommandsService.isFilled;

  // eslint-disable-next-line sonarjs/cognitive-complexity
  configCategoryTitles = computed(() => {
    const scheduleResponse = this.scheduleResponse();
    const schedules = this.scheduleServices.defaultSchedules();
    const constructionRules = this.virtualFenceService.constructionRules();
    const virtualFenceFilled = this.virtualFenceService.isVirtualFenceFilled();
    const titles: {
      label: string;
      value: dsEnums.ScheduleType | 'VIRTUAL_FENCE_MAP' | 'DROP_OFF';
    }[] = [];
    if (!scheduleResponse && !schedules) return [];
    if (
      (scheduleResponse?.GPS?.length && !this.isConfirmed() && !this.data?.readonly) ||
      schedules?.some((schedule) => schedule.scheduleType === 'GPS')
    ) {
      titles.push({ label: 'GPS Schedule', value: 'GPS' });
    }
    if (
      (scheduleResponse?.PROXIMITY?.length && !this.isConfirmed() && !this.data?.readonly) ||
      schedules?.some((schedule) => schedule.scheduleType === 'PROXIMITY')
    ) {
      titles.push({ label: 'Proximity Schedule', value: 'PROXIMITY' });
    }
    if (
      (scheduleResponse?.COMMUNICATION?.length && !this.isConfirmed() && !this.data?.readonly) ||
      schedules?.some((schedule) => schedule.scheduleType === 'COMMUNICATION')
    ) {
      titles.push({ label: 'Communication Schedule', value: 'COMMUNICATION' });
    }
    if (
      (scheduleResponse?.VHF?.length && !this.isConfirmed() && !this.data?.readonly) ||
      schedules?.some((schedule) => schedule.scheduleType === 'VHF')
    ) {
      titles.push({ label: 'Beacon Schedule', value: 'VHF' });
    }
    if (
      (scheduleResponse?.CAMERA?.length && !this.isConfirmed() && !this.data?.readonly) ||
      schedules?.some((schedule) => schedule.scheduleType === 'CAMERA')
    ) {
      titles.push({ label: 'Camera Schedule', value: 'CAMERA' });
    }
    if (
      !this.data?.isTerminal &&
      ((scheduleResponse?.VIRTUAL_FENCE?.length && !this.isConfirmed() && !this.data?.readonly) ||
        schedules?.some((schedule) => schedule.scheduleType === 'VIRTUAL_FENCE'))
    ) {
      titles.push({ label: 'Virtual Fence Schedule', value: 'VIRTUAL_FENCE' });
    }
    if (
      !this.data?.isTerminal &&
      ((scheduleResponse?.VIRTUAL_FENCE?.length && !this.isConfirmed() && !this.data?.readonly) ||
        schedules?.some((schedule) => schedule.scheduleType === 'VIRTUAL_FENCE') ||
        (virtualFenceFilled && this.isConfirmed()) ||
        (constructionRules?.length && !this.isConfirmed()))
    ) {
      titles.push({ label: 'Virtual Fence Map', value: 'VIRTUAL_FENCE_MAP' });
    }
    if (
      (scheduleResponse?.SOLEX_GPS?.length && !this.isConfirmed() && !this.data?.readonly) ||
      schedules?.some((schedule) => schedule.scheduleType === 'SOLEX_GPS')
    ) {
      titles.push({ label: 'Solex GPS Schedule', value: 'SOLEX_GPS' });
    }
    const collarsHaveDropOff = this.collarDetailsResponse()?.some(
      (c) =>
        (c as webDtos.CollarExtended).internalDropOffId ||
        (c as webDtos.CollarExtended).externalDropOffId,
    );
    if (
      collarsHaveDropOff &&
      !this.isConfirmed() &&
      !this.data?.readonly &&
      !this.data?.isTerminal
    ) {
      titles.push({ label: 'Drop Off', value: 'DROP_OFF' });
    }
    return titles;
  });

  abstract onSendRemoteCommand(): void;
  abstract onAddShare(idDevice: number): void;
  abstract onEditShare(event: { idDevice: number; email: string; oldRole: userEnums.Role }): void;
  abstract onDeleteShare(event: { idDevice: number; email: string }): void;
  abstract onSubmitFireDropOff(): void;
  abstract onLoadPositions(request: {
    deviceIds: number[];
    lastXRecords?: number;
    utcFrom?: string;
    utcTo?: string;
    isAnimation?: boolean;
  }): void;
  abstract allDropOffIds$: Observable<number[]>;
  abstract get isAdmin(): boolean;
  abstract get dialogResult(): webDtos.CustomerCommand[] | undefined;

  collarInputRef: (() => HTMLInputElement | undefined) | undefined;
  autocompleteTriggerRef: (() => MatAutocompleteTrigger | undefined) | undefined;

  setSelectedIds(ids: number[]) {
    this.remoteCommandProviderService.selectedCollarIds.set(ids);
    this.dropOffCommandService.selectedCollarIds$.next(ids);
  }

  init() {
    this.remoteCommandProviderService.isRemote.set(this.data?.remote ?? true);
    this.remoteCommandsService.ignoreShortHeaderMessage$?.next(Boolean(this.data?.isTerminal));
    this.scheduleServices.setSchedulesMandatory([]);
    this.onInitEarly();
    this.onChangeUpdateParameterDataList(this.data?.parameterDataList || [], this.data?.readonly);
    this.onInitAfterParameterData();
    this.detectInitialBasicOrAdvanced();
    this.scheduleServices.initializeSchedules(
      this.data?.schedules,
      !!this.data?.schedules?.length,
      this.data?.updateDefault ? !this.data?.updateDefault : !!this.data?.schedules?.length,
      true,
    );
    this.virtualFenceService.initialize(
      this.data?.virtualFenceConstructionRules || [],
      !this.data?.readonly && !!this.data?.virtualFenceConstructionRules?.length,
    );
    this.selectionSubscription = this.selectedCollarsAndGroups$
      .pipe(debounceTime(500))
      .subscribe((selectedCollarsAndGroups) => {
        let selectedCollarIds: number[] = [];
        selectedCollarsAndGroups?.forEach(({ collarId, group }) => {
          if (collarId) selectedCollarIds.push(collarId);
          else if (group?.membersIds) selectedCollarIds.push(...group.membersIds);
        });
        selectedCollarIds = selectedCollarIds.filter((item, pos, a) => a.indexOf(item) == pos);
        this.setSelectedIds(selectedCollarIds);
      });

    this.virtualFenceService.isEmptyVirtualFence.set(false);
    this.virtualFenceService.hasEmptyVirtualFence.set(true);
    this.remoteCommandProviderService.selectedCollarIds.set(this.data?.selectedDeviceIds || []);
    this.dropOffCommandService.selectedDropOffData$.next([]);
    this.devicesSubscription = this.dropOffCommandService.dropOffUpdater$.subscribe();

    this.remoteCommandsService.readonly$.next(this.data?.readonly);

    this.onInitAfterReadonly();

    this.validationSubscription = this.data?.isTerminal
      ? combineLatest([
          this.remoteCommandsService.remoteSchedule$,
          this.remoteCommandsService.remoteParameter$,
          this.remoteCommandsService.readonly$,
        ])
          .pipe(filter(([, , readonly]) => !readonly))
          .subscribe()
      : combineLatest([this.remoteCommandsService.validator$, this.remoteCommandsService.readonly$])
          .pipe(
            filter(([, readonly]) => !readonly),
            map(() => this.remoteCommandsService.generateCommand()),
            distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
          )
          .subscribe(() => this.onValidate());

    this.dropOffSubscription = this.setupDropOffSubscription();
  }

  protected onInitEarly(): void {}
  protected onInitAfterParameterData(): void {}
  protected onInitAfterReadonly(): void {}

  protected setupDropOffSubscription(): Subscription {
    return this.dropOffCommandService.updatedDropOffData$.subscribe((selectedDropOff) =>
      selectedDropOff?.forEach(({ dropOff, idDevice }) => {
        if (!dropOff?.idDevice) return;
        const responseValue = this.dropOffCommandService.remoteDeviceCommands();
        const command: dsDtos.Command = { deviceClassWithOwnIdPool: 'COLLAR', idDevice };
        let deviceCommandType: dsEnums.DeviceCommandType;
        if (responseValue?.DROP_OFF_RELEASE?.includes(idDevice)) {
          deviceCommandType = 'DROP_OFF_RELEASE';
        } else if (responseValue?.DROP_OFF_RELEASE_LONG?.includes(idDevice)) {
          deviceCommandType = 'DROP_OFF_RELEASE_LONG';
        }
        if (deviceCommandType) {
          command.deviceCommand = {
            data: dropOff.idDevice.toString(),
            deviceCommandType,
          };
          this.validationService
            .validate(command)
            .pipe(catchError(() => of(undefined)))
            .subscribe();
        }
      }),
    );
  }

  destroy() {
    this.destroySpecific();
    Object.values(this.validationApiSubscriptions || {}).forEach((sub) => sub?.unsubscribe());
    this.validationSubscription?.unsubscribe();
    this.dropOffSubscription?.unsubscribe();
    this.devicesSubscription?.unsubscribe();
    this.toggleSubscription?.unsubscribe();
    this.selectionSubscription?.unsubscribe();
    this.virtualFenceService.initialize([], false);
    this.remoteCommandsService.ignoreShortHeaderMessage$?.next(false);
    this.remoteCommandProviderService.isRemote.set(true);
    this.remoteCommandProviderService.selectedCollarIds.set([]);
    this.destroyBeforeScheduleReset();
    this.scheduleServices.resetSchedules();
    this.parameterDataService.reset();
  }

  protected destroySpecific(): void {}
  protected destroyBeforeScheduleReset(): void {}

  onResized(isFullScreen: boolean) {
    this.isFullScreen.set(isFullScreen);
    if (isFullScreen) {
      this.dialogRef.updateSize('100vw', '100vh');
    } else {
      this.dialogRef.updateSize(DIALOG_WIDER_TALL_CONFIG.width, DIALOG_WIDER_TALL_CONFIG.height);
    }
  }

  onChangeParameterDataListWithParameter($event: ParameterDataWithParameter[]) {
    this.parameterDataService.parameterDataList.set($event);
  }

  onChangeUpdateParameterDataList($event: dsDtos.ParameterData[], updateDefault: boolean) {
    this.remoteCommandsService.updateParameterDataList($event, updateDefault);
  }

  onChangeBasicOrAdvanced($event: 'basic' | 'advanced') {
    this.remoteCommandsService.basicOrAdvanced$.next($event);
  }

  onChangeCategory($event: string) {
    this.selectedTab.set($event);
  }

  add() {}

  onKeyUp(event: KeyboardEvent) {
    if (
      event.code === 'Enter' ||
      event.code === 'NumpadEnter' ||
      event.code === 'Space' ||
      event.code === 'Comma'
    ) {
      const enteredValue = this.collarCtrl.value;
      this.submitInput(enteredValue);
    }
  }

  onPaste($event: ClipboardEvent) {
    const clipboardData = $event.clipboardData;
    const pastedText = clipboardData.getData('text');
    this.submitInput(pastedText);
    $event.preventDefault();
  }

  protected submitInput(enteredValue: string) {
    const delimiterRegex = /[,\s;]+/;
    const listOfCollars = enteredValue?.split(delimiterRegex)?.filter(Boolean);
    listOfCollars?.forEach((collarIdStr) => {
      const collarId = parseInt(collarIdStr, 10);
      if (!isNaN(collarId)) {
        const alreadySelected = this.selectedCollarsAndGroups$.value?.some(
          (item) => item.collarId === collarId,
        );
        if (!alreadySelected) {
          this.selectedCollarsAndGroups$.next([
            ...((this.selectedCollarsAndGroups$.value as any) || []),
            { collarId },
          ]);
        }
      }
    });
    this.collarCtrl.setValue('');
    const inputEl = this.collarInputRef?.();
    if (inputEl) inputEl.value = '';
    this.autocompleteTriggerRef?.()?.closePanel();
  }

  onClickedSelectCollar() {}

  onRemoveCollarAndGroup({ collarId, group }: CollarAndGroup) {
    this.selectedCollarsAndGroups$.next(
      this.selectedCollarsAndGroups$.value.filter(
        (item) => item.collarId !== collarId || item.group?.id !== group?.id,
      ),
    );

    if (!this.selectedCollarsAndGroups$.value.length) {
      this.scheduleServices.resetSchedules();
      this.parameterDataService.reset();
      this.virtualFenceService.initialize([], false);
      this.virtualFenceService.hasEmptyVirtualFence.set(true);
    }
  }

  onClose() {
    this.dialogRef.close();
  }

  onClear() {
    ScheduleHelp.services()?.forEach((scheduleType) => {
      this.scheduleServices[scheduleType].onRemoveSchedule();
      this.scheduleServices[scheduleType].setToDefaultSchedule();
    });
    this.cameraScheduleService.onRemoveSchedule();
    this.cameraScheduleService.setToDefaultSchedule();
    this.parameterDataService.changedParameterDataList.set([]);
    this.virtualFenceService.resetToDefaultVirtualFence();
  }

  resetToDefaultPolygons() {
    this.virtualFenceService.resetVirtualFence();
    const polygons = this.virtualFenceService.dataAsPolygonStructure([
      {
        virtualFenceConstructionRules: this.data?.virtualFenceConstructionRules,
      },
    ]);
    this.virtualFenceService.updateAndShowPolygons(polygons, true);
  }

  get virtualFenceRules() {
    const allPolygons = this.virtualFenceService.allPolygons();
    if (allPolygons.length) {
      return this.virtualFenceService.prepareVirtualFenceRules(allPolygons);
    }
    return undefined;
  }

  getTooltip({ collarId, group }: CollarAndGroup) {
    const shares = this.remoteCommandsService
      .selectedCollars()
      ?.find((collar) => collar.id === collarId)?.shares;
    const owner = shares?.find((share) => share.role === 'OWNER');
    if (owner && collarId) {
      return owner.email;
    }
    if (collarId) return `Collar Id: ${collarId}`;
    const groupIdLine = group?.id ? `Group Id: ${group?.id}` : '';
    const memberIdsLine = group?.membersIds ? `Collar IDs: ${group?.membersIds?.join(', ')}` : '';
    return `${groupIdLine}
  ${memberIdsLine}`;
  }

  collarsWithShortHeaderFormatJoin() {
    return `[${this.remoteCommandsService.collarsWithNoShortHeaderFormat()?.join(', ')}]`;
  }

  sendTooltip() {
    const hasParamChanges = this.parameterDataService.isChanged();
    if (!this.data?.isTerminal && this.validationService.state() === 'error')
      return 'Invalid command';
    if (!this.data?.isTerminal && this.validationService.state() === 'loading')
      return 'Waiting for validation';
    if (!this.areSchedulesFilled() && !hasParamChanges && !this.isVirtualFenceFilled())
      return 'Empty command';
    return this.data?.isTerminal ? 'Write command on terminal' : 'Send';
  }

  get isSendEnabled() {
    if (!this.data?.isTerminal && this.remoteCommandsService.isGlobalstarSelected()) return false;
    const hasParamChanges = this.parameterDataService.isChanged();
    return (
      this.remoteCommandProviderService.selectedCollarIds()?.length &&
      this.selectedTab() !== 'Drop Off' &&
      ((this.areSchedulesFilled() && this.areSchedulesValid()) ||
        (!this.areSchedulesFilled() && hasParamChanges) ||
        (this.isVirtualFenceFilled() && this.isVirtualFenceChanged()))
    );
  }

  getTitle(selectedCollars: CollarAndGroup[]) {
    if (this.data?.isTerminal) {
      if (this.isConfirmed()) return 'Terminal Command Written';
      if (this.data?.readonly) {
        const selectedCollarsString = selectedCollars
          ?.map((selectedCollar) => selectedCollar.collarId)
          ?.join(',');
        return `Device ${selectedCollarsString} - Terminal Command`;
      }
      return this.data?.isEditTerminalCommand ? 'Edit Terminal Command' : 'New Terminal Command';
    }
    if (this.isConfirmed()) {
      return 'Remote Command Sent';
    }
    if (this.data?.readonly) {
      const selectedCollarsString = selectedCollars
        ?.map((selectedCollar) => selectedCollar.collarId)
        ?.join(',');
      return `Device ${selectedCollarsString} - Schedules`;
    }
    return 'New Remote Command';
  }

  onAddDropOff($event: { row: DropOffDataTable; dropOffId: string }) {
    this.dropOffCommandService.addDropOff($event.row, $event.dropOffId);
  }

  onRemove($event: DropOffDataTable) {
    this.dropOffCommandService.remove($event);
  }

  onValidate() {
    this.validationService.reset();
    const commands = this.remoteCommandsService.generateCommand();
    commands?.forEach((command) => {
      const collarId = command?.idDevice;
      if (
        command.schedules?.length ||
        command.parameterDataList?.length ||
        command.virtualFenceConstructionRules
      ) {
        this.validationApiSubscriptions[collarId]?.unsubscribe();
        this.validationApiSubscriptions[collarId] = this.validationService
          .validate(command)
          .pipe(catchError(() => of(undefined)))
          .subscribe();
      }
    });
  }

  onHelp() {
    this.tourGuideService.startTour();
  }

  protected detectInitialBasicOrAdvanced(): void {
    const parameterDataList = this.data?.parameterDataList || [];
    if (!parameterDataList.length) return;

    this.parameters$
      .pipe(
        filter((params) => !!params?.length),
        take(1),
      )
      .subscribe((params) => {
        const parameterDataIds = new Set(parameterDataList.map((p) => p.parameterId));
        const hasAdvanced = params.some(
          (p) => p.id && parameterDataIds.has(p.id) && p.advanced === true,
        );
        if (hasAdvanced) {
          this.remoteCommandsService.basicOrAdvanced$.next('advanced');
        }
      });
  }

  protected buildSchedules(): dsDtos.Schedule[] {
    const schedules: dsDtos.Schedule[] =
      ScheduleHelp.services()
        ?.filter((type) => this.scheduleServices[type]?.isFilledAndChanged())
        ?.map((scheduleType) => ({
          scheduleType,
          userDefined: true,
          rules: Object.values(this.scheduleServices[scheduleType].rules$.value).filter(Boolean),
        })) ?? [];

    if (this.scheduleServices.CAMERA.isFilledAndChanged()) {
      schedules.push({
        scheduleType: 'CAMERA',
        userDefined: true,
        cameraScheduleRules: Object.values(this.scheduleServices.CAMERA.rules$.value).filter(
          Boolean,
        ),
      });
    }
    return schedules;
  }

  protected buildSendCommonData() {
    const parameterDataList = this.parameterDataService.changedParameterDataList();
    const changedParameterDataListWithFactoryReset =
      this.parameterDataService.changedParameterDataListWithFactoryReset();
    const schedules = this.buildSchedules();
    const selectedIds = this.remoteCommandProviderService.selectedCollarIds() || [];
    const collars = this.remoteCommandsService.selectedCollars() || [];
    const selectedSet = new Set(selectedIds);
    const isGsmSelected = collars
      ?.filter((c) => selectedSet.has(c.id))
      ?.some((c) => c.comType === 'GSM');

    return {
      parameterDataList,
      changedParameterDataListWithFactoryReset,
      schedules,
      selectedIds,
      collars,
      isGsmSelected,
    };
  }

  ngOnDestroy() {
    this.destroy();
  }
}
